import { useEffect, useMemo, useState } from 'react';
import { fetchRevenueSummary } from '../api/revenueSummary';
import { computeTopEventsComparison, normalizeTopEvents } from '../lib/topEvents';

function parseIsoDate(value) {
  if (typeof value !== 'string') return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatIsoDate(value) {
  return value.toISOString().slice(0, 10);
}

function shiftToPreviousPeriod(start, end) {
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (!startDate || !endDate || endDate < startDate) return null;

  const ms = 24 * 60 * 60 * 1000;
  const spanDays = Math.floor((endDate.getTime() - startDate.getTime()) / ms) + 1;
  const previousEnd = new Date(startDate.getTime() - ms);
  const previousStart = new Date(previousEnd.getTime() - (spanDays - 1) * ms);

  return {
    start: formatIsoDate(previousStart),
    end: formatIsoDate(previousEnd),
  };
}

export function useTopEvents(options = {}) {
  const {
    filters = {},
    includeComparison = false,
    limit = 5,
    enabled = true,
    revenueSummary = null,
    revenueSummaryLoading = false,
  } = options;

  const [state, setState] = useState({
    isLoading: Boolean(enabled),
    error: null,
    rows: [],
    comparison: null,
    summary: null,
  });

  const embeddedSignature = useMemo(() => {
    if (!revenueSummary || !Array.isArray(revenueSummary.topEventsPreviousPeriod)) return '';
    const { topEvents, topEventsPreviousPeriod } = revenueSummary;
    if (!Array.isArray(topEvents)) return '';
    try {
      return JSON.stringify({ topEvents, topEventsPreviousPeriod, limit });
    } catch {
      return '';
    }
  }, [revenueSummary, limit]);

  useEffect(() => {
    if (!enabled) {
      setState({ isLoading: false, error: null, rows: [], comparison: null, summary: null });
      return;
    }

    const controller = new AbortController();

    async function loadTopEvents() {
      try {
        const activeFilters = filters || {};
        const activeLimit = limit || 5;
        const activeIncludeComparison = includeComparison || false;
        const summaryLoading = revenueSummaryLoading || false;

        const canUseEmbedded =
          !summaryLoading &&
          activeIncludeComparison &&
          activeFilters.start &&
          activeFilters.end &&
          revenueSummary &&
          Array.isArray(revenueSummary.topEvents) &&
          Array.isArray(revenueSummary.topEventsPreviousPeriod);

        if (canUseEmbedded) {
          const currentRows = normalizeTopEvents(revenueSummary.topEvents, activeLimit);
          const previousRows = normalizeTopEvents(revenueSummary.topEventsPreviousPeriod, activeLimit);
          const comparison = computeTopEventsComparison(currentRows, previousRows);
          setState({
            isLoading: false,
            error: null,
            rows: currentRows,
            comparison,
            summary: revenueSummary.summary ?? null,
          });
          return;
        }

        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const currentRequest = fetchRevenueSummary(
          { ...activeFilters, limit: activeLimit },
          { signal: controller.signal }
        );

        let previousRequest = Promise.resolve(null);
        if (activeIncludeComparison) {
          const previousPeriod = shiftToPreviousPeriod(activeFilters.start, activeFilters.end);
          if (previousPeriod) {
            previousRequest = fetchRevenueSummary(
              { ...activeFilters, ...previousPeriod, limit: activeLimit },
              { signal: controller.signal }
            );
          }
        }

        const [currentResult, previousResult] = await Promise.all([currentRequest, previousRequest]);
        const currentPayload = currentResult?.data ?? null;
        const previousPayload = previousResult?.data ?? null;

        const currentRows = normalizeTopEvents(currentPayload?.topEvents, activeLimit);
        const previousRows = previousPayload?.topEvents
          ? normalizeTopEvents(previousPayload.topEvents, activeLimit)
          : [];

        const comparison = activeIncludeComparison
          ? computeTopEventsComparison(currentRows, previousRows)
          : null;

        setState({
          isLoading: false,
          error: null,
          rows: currentRows,
          comparison,
          summary: currentPayload?.summary ?? null,
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        setState({ isLoading: false, error, rows: [], comparison: null, summary: null });
      }
    }

    loadTopEvents();
    return () => { controller.abort(); };
  }, [enabled, filters, includeComparison, limit, embeddedSignature, revenueSummary, revenueSummaryLoading]);

  return state;
}